import _ from 'underscore';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  Body1Strong,
} from '@fluentui/react-components';
import { ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { type ILogger } from '@bingads-webui-react/msa-copilot-common';

import { IAIMessage } from '@bingads-webui/ads-copilot-ui';

import { Fluctuation } from '../../types';
import { useFluctuationsMessage } from '../../hooks/use-fluctuations-message';
import { IDateRange } from './types';
import { Summary } from './summary';
import { InsightsChart } from '../insights-chart';

const useStyles = makeStyles({
  fluctuationContent: {
    display: 'flex',
    flexWrap: 'wrap',
    ...shorthands.gap(tokens.spacingHorizontalXXL),
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100vh',
  },
  fluctuationChartContainer: {
    ...shorthands.flex(1, 0, '400px'),
    maxWidth: '100%',
    maxHeight: '100%',
    height: '100%',
    overflowY: 'hidden',
    overflowX: 'hidden',
    paddingRight: tokens.spacingVerticalS,
    display: 'flex',
    flexDirection: 'column',
  },
  fluctuationHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  carouselControl: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    height: '100%',
  },
  summaryContainer: {
    ...shorthands.flex(1, 0, '400px'),
    maxWidth: '100%',
    display: 'flex',
    overflowY: 'auto',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    '@media only screen and (max-width: 1400px)': {
      height: 'unset',
      overflowY: 'visible',
    },
  },
});

export type FluctuationProps = {
  i18n: {
    getString: (key: string) => string,
  },
  replyToId?: string,
  TimeZone: number,
  dateRange: IDateRange,
  dateFormat: string,
  logger?: ILogger,
  onFeedbackButtonClick: IAIMessage['onFeedbackButtonClick'];
};

const getTitle = (fluctuation: Fluctuation, i18n: any) => {
  if (!fluctuation) {
    return '';
  }

  let metric = '';
  switch (fluctuation.metricType) {
    case 'Impression':
      metric = i18n.getString(_TL_('Impressions'));
      break;
    case 'Click':
      metric = i18n.getString(_TL_('Clicks'));
      break;
    case 'Conversion':
    default:
      metric = i18n.getString(_TL_('Conversions'));
      break;
  }

  let changeDirection = '';
  switch (fluctuation.shiftDirection) {
    case 'Increase':
      changeDirection = i18n.getString(_TL_('increased'));
      break;
    case 'Decrease':
    default:
      changeDirection = i18n.getString(_TL_('decreased'));
      break;
  }

  const parser = i18n.dateParser({
    raw: 'yyyy-MM-dd',
  });
  const parsedDate = parser(fluctuation.shiftStartDate);
  const date = i18n.formatDate(parsedDate);

  return curlyTemplate(i18n.getString(_TL_('{{metric}} {{changeDirection}} on {{date}}')))({
    metric,
    changeDirection,
    date,
  });
};

export const Fluctuations: React.FC<FluctuationProps> = ({
  replyToId,
  i18n,
  TimeZone,
  dateRange,
  dateFormat,
  logger,
  onFeedbackButtonClick,
}) => {
  const [pageNumber, setPageNumber] = useState(0);

  const {
    fluctuationResult, fluctuationSummaryMessage, fluctuationsChartData, isCompleted,
  } = useFluctuationsMessage(replyToId);

  const pageCount = _.isArray(fluctuationResult?.Result) ? fluctuationResult.Result.length : 0;

  const fluctuations = fluctuationResult?.Result || [];
  const classes = useStyles();
  const title = getTitle(fluctuations[pageNumber], i18n);

  const isLoading = useMemo(() => !fluctuationsChartData?.length && !isCompleted, [fluctuationsChartData, isCompleted]);
  const shouldRenderNavigation = pageCount > 1;

  if (!fluctuationSummaryMessage) {
    logger?.trace(`[Insights Fluctuations]: No fluctuationSummaryMessage, Fluctuations not rendered. replyToId: ${replyToId}`);
    return null;
  }

  useEffect(() => {
    if (isCompleted && shouldRenderNavigation) {
      logger?.trace(`[Insights Fluctuations]: Fluctuation Navigation rendered. fluctuationCount: ${pageCount}, replyToId: ${replyToId}`);
    }
    if (isCompleted && !shouldRenderNavigation) {
      logger?.trace(`[Insights Fluctuations]: Fluctuation Navigation not rendered. fluctuationCount: ${pageCount}, replyToId: ${replyToId}`);
    }
  }, [
    logger,
    replyToId,
    shouldRenderNavigation,
  ]);

  const handlePageBackward = () => {
    logger?.trace(`[Insights Fluctuations]: User navigates backward in Fluctuation Navigation. replyToId: ${replyToId}`);
    return setPageNumber(pageNumber - 1);
  };
  const handlePageForward = () => {
    logger?.trace(`[Insights Fluctuations]: User navigates forward in Fluctuation Navigation. replyToId: ${replyToId}`);
    return setPageNumber(pageNumber + 1);
  };

  return (
    <div className={mergeClasses('fluctuation-content', classes.fluctuationContent)}>
      <div className={mergeClasses('chart-container', classes.fluctuationChartContainer)}>
        <div className={mergeClasses('fluctuation-header', classes.fluctuationHeader)}>
          <Body1Strong className="fluctuation-title">{title}</Body1Strong>
          { !isLoading && shouldRenderNavigation && (
            <div className={mergeClasses('fluctuation-carousel-arrows', classes.carouselControl)}>
              <div>
                {curlyTemplate(i18n.getString(_TL_('{{page}} of {{pageCount}}')))({
                  page: pageNumber + 1,
                  pageCount,
                })}
              </div>
              <Button
                className="carousel-left"
                icon={<ChevronLeftRegular />}
                disabled={pageNumber === 0}
                appearance="transparent"
                onClick={handlePageBackward}
                aria-label={i18n.getString(_TL_('previous page'))}
              />
              <Button
                className="carousel-right"
                icon={<ChevronRightRegular />}
                appearance="transparent"
                disabled={pageNumber === pageCount - 1}
                onClick={handlePageForward}
                aria-label={i18n.getString(_TL_('next page'))}
              />
            </div>
          )}
        </div>
        <InsightsChart
          className="fluctuation-chart"
          isLoading={isLoading}
          performanceTimeSeries={fluctuationsChartData?.[pageNumber]}
          TimeZone={TimeZone}
        />
      </div>
      <div className={mergeClasses('summary-container', classes.summaryContainer)}>
        <Summary
          message={fluctuationSummaryMessage}
          fluctuation={fluctuations[pageNumber]}
          dateFormat={dateFormat}
          dateRange={dateRange}
          logger={logger}
          replyToId={replyToId}
          onFeedbackButtonClick={onFeedbackButtonClick}
        />
      </div>
    </div>
  );
};
